import { findDuplicateOptions, multiSelectOptionValue } from '@repo/lib';

// Selections allowed: "one" keeps a Dropdown select or Multiple choice; any other mode makes it a
// multi-select with its counts on minValue / maxValue. Required stays independent of the counts.

export const SINGLE_MODE = 'one';

export const SELECTION_MODES = [
  { value: SINGLE_MODE, label: 'One' },
  { value: 'any', label: 'Any number' },
  { value: 'atleast', label: 'At least' },
  { value: 'upto', label: 'Up to' },
  { value: 'exactly', label: 'Exactly' },
  { value: 'between', label: 'Between' },
];

// A count of 1 would turn the field back into a single pick, so new bounds
// start at 2 (3 for the upper end of a range).
const DEFAULT_COUNT = 2;
const DEFAULT_RANGE_MAX = 3;

const bound = (enabled, value) => ({ enabled, value: enabled ? String(value) : '' });
const number = b => (b?.enabled ? Number(b.value) || 0 : 0);

export function selectionModeFromField({ element, minValue, maxValue }) {
  if (element !== 'multi-select') return SINGLE_MODE;
  return selectionModeFromBounds(minValue, maxValue);
}

/** 'dropdown' or 'list': what the field looks like, independent of how many it allows. */
export function layoutForField({ element, layout }) {
  // Saving a single element leaves an earlier multi-select's `layout` on the field.
  if (element === 'multi-select' && (layout === 'list' || layout === 'dropdown')) return layout;
  return element === 'multiple-choice' ? 'list' : 'dropdown';
}

export function elementForMode(mode, layout = 'dropdown') {
  if (mode === SINGLE_MODE) return layout === 'list' ? 'multiple-choice' : 'select';
  return 'multi-select';
}

export function selectionModeFromBounds(minValue, maxValue) {
  const min = number(minValue);
  const max = number(maxValue);
  if (min > 0 && max > 0) return min === max ? 'exactly' : 'between';
  if (min > 0) return 'atleast';
  if (max > 0) return 'upto';
  return 'any';
}

const clamp = (value, optionCount) =>
  optionCount > 0 ? Math.max(1, Math.min(value, optionCount)) : Math.max(1, value);

/** Bounds to show when the merchant switches mode; keeps a usable number already typed. */
export function boundsForMode(mode, minValue, maxValue, optionCount = 0) {
  const min = number(minValue);
  const max = number(maxValue);
  const keep = (current, fallback) => clamp(current >= 2 ? current : fallback, optionCount);

  switch (mode) {
    case 'atleast':
      return { minValue: bound(true, keep(min, DEFAULT_COUNT)), maxValue: bound(false) };
    case 'upto':
      return { minValue: bound(false), maxValue: bound(true, keep(max, DEFAULT_COUNT)) };
    case 'exactly': {
      const count = keep(min || max, DEFAULT_COUNT);
      return { minValue: bound(true, count), maxValue: bound(true, count) };
    }
    case 'between': {
      const low = keep(min, DEFAULT_COUNT);
      const high = Math.max(low + 1, keep(max, DEFAULT_RANGE_MAX));
      return {
        minValue: bound(true, low),
        maxValue: bound(true, optionCount > 0 ? Math.min(high, optionCount) : high),
      };
    }
    default:
      return { minValue: bound(false), maxValue: bound(false) };
  }
}

// A count dropdown shows its first choice for a number outside its list, so each count moves to
// the nearest number it offers (a range settles low, high, low). Returns null when nothing changes.
export function fitSelections(mode, minValue, maxValue, optionCount) {
  if (mode === SINGLE_MODE || mode === 'any') return null;
  const fit = (position, current, other, fallback) => {
    const choices = countChoices(mode, position, optionCount, other).map(choice =>
      Number(choice.value),
    );
    const first = choices[0];
    const last = choices[choices.length - 1];
    const wanted = current > 0 ? current : fallback;
    return Math.min(last, Math.max(first, wanted));
  };
  let min = number(minValue);
  let max = number(maxValue);
  const hasMin = mode === 'atleast' || mode === 'exactly' || mode === 'between';
  const hasMax = mode === 'upto' || mode === 'between';
  if (hasMin) min = fit('low', min, max, DEFAULT_COUNT);
  if (hasMax) max = fit('high', max, min, mode === 'between' ? DEFAULT_RANGE_MAX : DEFAULT_COUNT);
  if (mode === 'between') min = fit('low', min, max, DEFAULT_COUNT);
  if (mode === 'exactly') max = min;
  const next = {
    minValue: bound(hasMin || mode === 'exactly', min),
    maxValue: bound(hasMax || mode === 'exactly', max),
  };
  // Compared by meaning, not shape: a stored `{ enabled: false }` with no value, or a
  // numeric value, must not read as a change, or the editor opens already dirty.
  const same = (a, b) =>
    Boolean(a?.enabled) === Boolean(b?.enabled) &&
    (!a?.enabled || Number(a.value) === Number(b?.value));
  return same(next.minValue, minValue) && same(next.maxValue, maxValue) ? null : next;
}

/** What gets saved for a mode from whatever the inputs hold. */
export function normalizeSelections(mode, minValue, maxValue) {
  const min = Number(minValue?.value) || 0;
  const max = Number(maxValue?.value) || 0;
  switch (mode) {
    case 'atleast':
      return { minValue: bound(min > 0, min), maxValue: bound(false) };
    case 'upto':
      return { minValue: bound(false), maxValue: bound(max > 0, max) };
    case 'exactly':
      return { minValue: bound(min > 0, min), maxValue: bound(min > 0, min) };
    case 'between':
      return { minValue: bound(min > 0, min), maxValue: bound(max > 0, max) };
    default:
      return { minValue: bound(false), maxValue: bound(false) };
  }
}

/**
 * The counts a mode's dropdown offers: 1 upwards where 1 is sensible, else 2
 * upwards. For a range the two dropdowns constrain each other, so "3 and 2"
 * cannot be picked: the lower one stops below the upper, the upper starts
 * above the lower.
 */
export function countChoices(mode, position, optionCount, other) {
  let lowest = mode === 'atleast' || (mode === 'between' && position === 'low') ? 1 : 2;
  let highest = Math.max(lowest, optionCount || lowest);
  const otherCount = Number(other) || 0;
  if (mode === 'between' && otherCount > 0) {
    if (position === 'low') highest = Math.max(lowest, Math.min(highest, otherCount - 1));
    if (position === 'high') lowest = Math.min(highest, Math.max(lowest, otherCount + 1));
  }
  return Array.from({ length: Math.max(0, highest - lowest + 1) }, (_, index) => {
    const value = String(lowest + index);
    return { value, label: value };
  });
}

/** Formik-shaped errors, or an empty object. */
export function validateSelections({ mode, minValue, maxValue, optionCount = 0 }) {
  const min = number(minValue);
  const max = number(maxValue);
  const errors = {};
  // Only reachable with a single option now that counts follow their dropdowns.
  const tooMany = `Cannot be more than the number of options (${optionCount}). Add options, or set Selections allowed to One`;

  if (mode === SINGLE_MODE || mode === 'any') return errors;

  if (mode === 'atleast' || mode === 'between' || mode === 'exactly') {
    if (min < 1 || !Number.isInteger(min))
      errors.minValue = { value: 'Enter a whole number of 1 or more' };
    else if (optionCount > 0 && min > optionCount) errors.minValue = { value: tooMany };
  }
  if (mode === 'upto' || mode === 'between' || mode === 'exactly') {
    const value = mode === 'exactly' ? min : max;
    const key = mode === 'exactly' ? 'minValue' : 'maxValue';
    if (value < 2 || !Number.isInteger(value)) {
      errors[key] = { value: 'A multi-select needs 2 or more; use Dropdown select for one' };
    } else if (optionCount > 0 && value > optionCount) {
      errors[key] = { value: tooMany };
    }
  }
  if (mode === 'between' && !errors.minValue && !errors.maxValue && max <= min) {
    errors.maxValue = { value: 'The upper number must be more than the lower one' };
  }

  return errors;
}

/** Elements whose minValue / maxValue mean something; any other element drops them on save. */
export const ELEMENTS_WITH_BOUNDS = ['multi-select', 'date', 'date-time', 'time', 'quantity'];

export function keepsBounds(element) {
  return ELEMENTS_WITH_BOUNDS.includes(element);
}

const namedOptions = options => (options ?? []).filter(option => option.label);

const optionValues = options => new Set(namedOptions(options).map(multiSelectOptionValue));

/** The default ticks picker: shows each option's label and stores its value, as the checkout does. */
export function defaultSelectionChoices(options) {
  return namedOptions(options).map(option => ({
    name: option.label,
    value: multiSelectOptionValue(option),
  }));
}

/**
 * Drops default ticks whose option was removed, or whose value changed, in this edit
 * (a valueless option's value is its label); the API refuses them.
 */
export function pruneDefaultSelections(defaultValue, options = []) {
  if (!Array.isArray(defaultValue?.values)) return defaultValue;
  const known = optionValues(options);
  return { ...defaultValue, values: defaultValue.values.filter(value => known.has(value)) };
}

/**
 * The default must be answerable: no more ticks than the maximum, or the
 * checkout would load already over it. Formik-shaped errors, or an empty object.
 */
export function validateDefaultSelections({ defaultValue, maxValue, options = [] }) {
  if (!defaultValue?.enabled || !Array.isArray(defaultValue.values)) return {};
  const known = optionValues(options);
  const chosen = defaultValue.values.filter(value => known.has(value));
  const max = number(maxValue);
  if (max > 0 && chosen.length > max) {
    return {
      defaultValue: {
        values: `Pick at most ${max} default option${max === 1 ? '' : 's'}, the most a customer may choose`,
      },
    };
  }
  return {};
}

/**
 * Options the checkout would treat as one, as Formik errors on the repeated option, or an empty
 * object. A list hides option values, so a value clash there is shown under the label.
 */
export function validateUniqueOptions({ element, layout, options }) {
  const duplicates = findDuplicateOptions(element, options);
  if (duplicates.length === 0) return {};
  const showsValues = layoutForField({ element, layout }) === 'dropdown';
  const optionErrors = [];
  for (const { index, source } of duplicates) {
    if (source === 'label') {
      optionErrors[index] = { label: 'Labels must be unique' };
    } else if (!showsValues) {
      optionErrors[index] = {
        label: 'Values must be unique. Set Field type to Dropdown select to change them',
      };
    } else {
      optionErrors[index] = { value: 'Values must be unique' };
    }
  }
  return { options: optionErrors };
}
